import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import type { AuthError, AuthErrorKind, AuthSource } from '../contract.js';
import { copy } from '../copy.js';
import { cls } from '../parts/classes.js';
import { color, font, layer } from '../tokens.js';
import { GoogleButton } from './GoogleButton.js';
import { Wordmark } from './Wordmark.js';

/**
 * Sign in, sign up, the "check your email" notice, and resetting a password.
 *
 * NOT FROM THE PROTOTYPE. The owner asked for this screen on 2 October 2026 ("Make a login/sign up
 * screen… there is name + handle, you can get a handle of your choice unless it is already taken")
 * and reviews it. It borrows the v1 look and nothing else: the title where every title sits
 * (440, 150), the 560 px column, the underlined fields of the profile editor, the labels of the
 * weekday row, and the 44 px pill buttons. The KALNDAR wordmark sits above the title, and the handle
 * field has no hint under it (owner, 4 October 2026).
 *
 * Owner, 4 October 2026 (with a picture of a "Create an account" card): the screen opens on Create
 * account; under the title a question with an outlined button leads to the other step ("Already have
 * an account?" Sign in); then the fields, one full-width filled button, a divider and Google's button.
 * No step of this screen has the island at the bottom any more. Every step is its own <form> with its
 * button inside, the email is the user name and the handle is not, so that browsers and password
 * managers offer to save and fill the password.
 *
 * P8 adds a "Finish your account" step for a verified account the backend holds no profile for (its
 * handle claim expired, a Google account, an older account): name and handle, then the app loads.
 */

export type AuthMode = 'signIn' | 'signUp' | 'verify' | 'forgot' | 'reset' | 'finish';

export interface AuthScreenProps {
  readonly auth: AuthSource;
  /** Called when a session exists: the app loads. */
  readonly onSignedIn: () => void;
  /** A password-reset link was opened: the token from its address. */
  readonly resetToken?: string | null;
  /** The reset link has been used (or given up): the address can be cleaned. */
  readonly onResetDone?: () => void;
  /** Signed in, but the account has no profile yet: the screen opens on "Finish your account". */
  readonly finishing?: boolean;
  /** Leave the half-finished account (the Finish step's quiet button). */
  readonly onSignOut?: () => void;
  /**
   * A sign-in that failed before this screen opened (a return from Google): the fixed sentence the
   * sign-in step opens with. Read when the screen mounts, then cleared like any other error.
   */
  readonly initialError?: string | null;
  /**
   * The step a visitor without a reset link or an unfinished account sees first: Create account, unless
   * they came to sign in (they have just signed out, or `initialError` is set). Read when the screen mounts.
   */
  readonly initialMode?: 'signIn' | 'signUp';
}

export const HANDLE_PATTERN = /^[a-z][a-z0-9_]{2,29}$/;
export const NAME_MAX = 24;
export const PASSWORD_MIN = 8;

type Field = 'name' | 'handle' | 'email' | 'password';
type Errors = Partial<Record<Field | 'form', string>>;

const FIELD_OF: Partial<Record<AuthErrorKind, Field>> = {
  handle_taken: 'handle', handle_invalid: 'handle', email_taken: 'email', email_invalid: 'email', password_weak: 'password',
};
function authError(value: unknown): AuthError {
  if (value && typeof value === 'object' && 'kind' in value && typeof (value as { kind: unknown }).kind === 'string') return value as AuthError;
  return { kind: 'server' };
}

const label: CSSProperties = { display: 'block', height: 14, fontFamily: font.label, fontSize: 11, lineHeight: '14px', letterSpacing: '0.12em', textTransform: 'uppercase', color: color.textFaint };
const input: CSSProperties = { display: 'block', width: '100%', height: 44, marginTop: 6, boxSizing: 'border-box', padding: '0 4px', border: 'none', background: 'transparent', boxShadow: `inset 0 -1px 0 ${color.line}`, color: color.textStrong, fontFamily: 'inherit', fontSize: 17 };
const note: CSSProperties = { minHeight: 20, marginTop: 4, fontSize: 13, lineHeight: '20px' };
const quiet: CSSProperties = { flex: 'none', height: 44, padding: '0 14px', border: 'none', borderRadius: 22, background: 'transparent', color: color.textMuted, fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' };
/** A second action nobody should have to look for: the quiet button with a ring and the strong text colour. */
const outlined: CSSProperties = { ...quiet, padding: '0 18px', boxShadow: `inset 0 0 0 1px ${color.lineStrong}`, color: color.textStrong, fontWeight: 500 };
/** The one filled button of a step, as wide as the column. */
const filled = (off: boolean): CSSProperties => ({
  display: 'block', width: '100%', height: 44, padding: '0 20px', border: 'none', borderRadius: 22, background: off ? color.tileHover : color.paper,
  color: off ? color.textFaint : color.ink, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: off ? 'default' : 'pointer',
});

function TextField({ id, name, text, type = 'text', value, onChange, placeholder, autoComplete, maxLength, minLength, help, error, disabled, autoFocus, lower }: {
  readonly id: string; readonly name: Field; readonly text: string; readonly type?: string; readonly value: string; readonly onChange: (value: string) => void;
  readonly placeholder?: string; readonly autoComplete: string; readonly maxLength?: number; readonly minLength?: number; readonly help?: string; readonly error?: string | undefined;
  readonly disabled: boolean; readonly autoFocus?: boolean; readonly lower?: boolean;
}) {
  return <div style={{ marginBottom: 14 }}>
    <label htmlFor={id} style={label}>{text}</label>
    <input id={id} name={name} className={cls('in2')} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete} maxLength={maxLength} minLength={minLength} disabled={disabled} autoFocus={autoFocus}
      autoCapitalize={lower ? 'none' : undefined} spellCheck={false} aria-invalid={!!error} aria-describedby={`${id}-note`}
      onChange={(event) => onChange(lower ? event.target.value.toLowerCase() : event.target.value)} style={input} />
    <div id={`${id}-note`} role={error ? 'alert' : undefined} style={{ ...note, color: error ? color.danger : color.textFaint }}>{error ?? help ?? ''}</div>
  </div>;
}

export function AuthScreen({ auth, onSignedIn, resetToken = null, onResetDone, finishing = false, onSignOut, initialError = null, initialMode }: AuthScreenProps) {
  // A failed sign-in that came back with its sentence is shown where it belongs: on the sign-in step.
  const [mode, setMode] = useState<AuthMode>(resetToken ? 'reset' : finishing ? 'finish' : initialMode ?? (initialError ? 'signIn' : 'signUp'));
  const [name, setName] = useState(''), [handle, setHandle] = useState(''), [email, setEmail] = useState(''), [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false), [notice, setNotice] = useState('');
  const [errors, setErrors] = useState<Errors>(() => (initialError && mode === 'signIn' ? { form: initialError } : {}));
  const formId = useId(), ids = { name: useId(), handle: useId(), email: useId(), password: useId() };
  const heading = useRef<HTMLHeadingElement | null>(null);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const go = (next: AuthMode, message = '') => { setMode(next); setErrors({}); setNotice(message); setPassword(''); };
  const fail = (reason: unknown) => {
    const error = authError(reason), field = FIELD_OF[error.kind];
    setErrors({ [field ?? 'form']: error.text ?? copy.auth.error[error.kind] });
  };
  /** Runs one request; inputs wait meanwhile. */
  async function run(request: () => Promise<void>, after: () => void): Promise<void> {
    if (busy) return;
    setBusy(true); setErrors({}); setNotice('');
    try { await request(); if (live.current) after(); }
    catch (reason) { if (live.current) fail(reason); }
    finally { if (live.current) setBusy(false); }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const mail = email.trim();
    if (mode === 'signIn') void run(() => auth.signIn(mail, password), onSignedIn);
    else if (mode === 'signUp') {
      const found: Errors = {};
      if (!name.trim()) found.name = copy.auth.error.nameMissing;
      if (!HANDLE_PATTERN.test(handle)) found.handle = copy.auth.error.handle_invalid;
      if (!mail.includes('@')) found.email = copy.auth.error.email_invalid;
      if (password.length < PASSWORD_MIN) found.password = copy.auth.error.password_weak;
      if (Object.keys(found).length) { setErrors(found); return; }
      void run(() => auth.signUp({ name: name.trim(), handle, email: mail, password }), () => go('verify'));
    }
    else if (mode === 'finish') {
      const found: Errors = {};
      if (!name.trim()) found.name = copy.auth.error.nameMissing;
      if (!HANDLE_PATTERN.test(handle)) found.handle = copy.auth.error.handle_invalid;
      if (Object.keys(found).length) { setErrors(found); return; }
      const finish = auth.finishProfile;
      if (finish) void run(() => finish.call(auth, { name: name.trim(), handle }), onSignedIn);
      else setErrors({ form: copy.auth.error.server });
    }
    else if (mode === 'verify') void run(() => auth.resendVerification(mail), () => setNotice(copy.auth.resent));
    else if (mode === 'forgot') {
      if (!mail.includes('@')) { setErrors({ email: copy.auth.error.email_invalid }); return; }
      void run(() => auth.requestPasswordReset(mail), () => setNotice(copy.auth.resetSent));
    }
    else {
      if (password.length < PASSWORD_MIN) { setErrors({ password: copy.auth.error.password_weak }); return; }
      void run(() => auth.resetPassword(resetToken ?? '', password), () => { onResetDone?.(); go('signIn', copy.auth.resetDone); });
    }
  }

  // A new step is announced by moving focus to its title.
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } if (mode === 'verify') heading.current?.focus(); }, [mode]);

  const title = { signIn: copy.auth.signInTitle, signUp: copy.auth.signUpTitle, verify: copy.auth.verifyTitle, forgot: copy.auth.forgotTitle, reset: copy.auth.resetTitle, finish: copy.auth.finishTitle }[mode];
  const primary = {
    signIn: busy ? copy.auth.signingIn : copy.auth.signIn, signUp: busy ? copy.auth.creating : copy.auth.createAccount,
    verify: busy ? copy.auth.sending : copy.auth.resend, forgot: busy ? copy.auth.sending : copy.auth.sendLink, reset: busy ? copy.auth.saving : copy.auth.savePassword,
    finish: busy ? copy.auth.finishing : copy.auth.finish,
  }[mode];
  const ready = mode === 'signIn' ? !!email.trim() && !!password : mode === 'signUp' ? !!name.trim() && !!handle && !!email.trim() && !!password
    : mode === 'finish' ? !!name.trim() && !!handle : mode === 'reset' ? !!password : mode === 'forgot' ? !!email.trim() : true;
  const off = busy || !ready;
  const unverified = mode === 'signIn' && errors.form === copy.auth.error.unverified;
  const leaveReset = () => { onResetDone?.(); go('signIn'); };

  // The two steps a visitor chooses between; each names the other in the row under its title.
  const entry = mode === 'signIn' || mode === 'signUp';

  return <>
    <Wordmark />
    <div style={{ position: 'absolute', left: 440, top: 150, width: 560, height: 44, zIndex: layer.header, display: 'flex', alignItems: 'center' }}>
      <h1 ref={heading} tabIndex={-1} data-v1-title="" style={{ margin: 0, fontSize: 26, fontWeight: 500, letterSpacing: '-0.01em', color: color.textStrong, whiteSpace: 'nowrap', outline: 'none' }}>{title}</h1>
    </div>
    {entry && <div data-v1-auth-ask="" style={{ position: 'absolute', left: 440, top: 198, width: 560, height: 44, zIndex: layer.header, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 14, lineHeight: '20px', color: color.textMuted }}>{mode === 'signUp' ? copy.auth.haveAccountAsk : copy.auth.newHereAsk}</span>
      <button type="button" className={cls('ctl', 'qb')} data-v1-auth-switch="" disabled={busy} onClick={() => go(mode === 'signUp' ? 'signIn' : 'signUp')} style={outlined}>{mode === 'signUp' ? copy.auth.signIn : copy.auth.createAccount}</button>
    </div>}
    {/* One form element per step (the key): a password manager sees a sign-in form or a sign-up form, never one that changes under it. */}
    <form key={mode} id={formId} data-v1-auth={mode} method="post" noValidate onSubmit={submit} style={{ position: 'absolute', left: 440, top: entry ? 256 : 236, width: 560, zIndex: layer.surface }}>
      {mode === 'verify' && <p style={{ margin: '0 0 14px', fontSize: 17, lineHeight: 1.45, color: color.text }}>{copy.auth.verifyBody(email.trim())}</p>}
      {mode === 'finish' && <p style={{ margin: '0 0 14px', fontSize: 17, lineHeight: 1.45, color: color.text }}>{copy.auth.finishBody}</p>}
      {(mode === 'signUp' || mode === 'finish') && <>
        <TextField id={ids.name} name="name" text={copy.auth.name} value={name} onChange={setName} placeholder={copy.auth.namePlaceholder} autoComplete="name" maxLength={NAME_MAX} error={errors.name} disabled={busy} autoFocus />
        {/* Not the user name: people sign in with their email, and that is what a password manager must keep. */}
        <TextField id={ids.handle} name="handle" text={copy.auth.handle} value={handle} onChange={setHandle} placeholder={copy.auth.handlePlaceholder} autoComplete="nickname" maxLength={30} error={errors.handle} disabled={busy} lower />
      </>}
      {(mode === 'signIn' || mode === 'signUp' || mode === 'forgot') && <TextField id={ids.email} name="email" text={copy.auth.email} type="email" value={email} onChange={setEmail} placeholder={copy.auth.emailPlaceholder} autoComplete="username" error={errors.email} disabled={busy} autoFocus={mode !== 'signUp'} />}
      {(mode === 'signIn' || mode === 'signUp' || mode === 'reset') && <TextField id={ids.password} name="password" text={mode === 'reset' ? copy.auth.newPassword : copy.auth.password} type="password" value={password} onChange={setPassword}
        autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'} {...(mode === 'signIn' ? {} : { help: copy.auth.passwordHelp, minLength: PASSWORD_MIN })} error={errors.password} disabled={busy} autoFocus={mode === 'reset'} />}
      {errors.form && <div role="alert" data-v1-auth-error="" style={{ marginBottom: 10, fontSize: 14, lineHeight: 1.45, color: color.danger }}>{errors.form}</div>}
      {notice && <div role="status" data-v1-auth-notice="" style={{ marginBottom: 10, fontSize: 14, lineHeight: 1.45, color: color.textStatus }}>{notice}</div>}
      {mode === 'verify'
        ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="submit" className={cls('ctl', 'qb')} disabled={busy} style={outlined}>{primary}</button>
          <button type="button" className={cls('ctl', 'qb')} data-v1-auth-switch="" disabled={busy} onClick={() => go('signIn')} style={quiet}>{copy.auth.back}</button>
        </div>
        : <button type="submit" className={cls('ctl', 'pb')} data-v1-auth-submit="" disabled={off} style={filled(off)}>{primary}</button>}
      {mode === 'signIn' && <div style={{ display: 'flex', gap: 2, marginTop: 6, marginLeft: -14 }}>
        <button type="button" className={cls('ctl', 'qb')} disabled={busy} onClick={() => go('forgot')} style={quiet}>{copy.auth.forgot}</button>
        {unverified && <button type="button" className={cls('ctl', 'qb')} disabled={busy} onClick={() => { void run(() => auth.resendVerification(email.trim()), () => setNotice(copy.auth.resent)); }} style={{ ...quiet, color: color.textStrong }}>{copy.auth.resend}</button>}
      </div>}
      {entry && auth.signInWithGoogle && <>
        <div data-v1-auth-or="" style={{ display: 'flex', alignItems: 'center', gap: 14, height: 14, margin: mode === 'signIn' ? '8px 0 14px' : '14px 0' }}>
          <span style={{ flex: '1 1 0', height: 1, background: color.line }} />
          <span style={{ fontFamily: font.label, fontSize: 11, lineHeight: '14px', letterSpacing: '0.12em', textTransform: 'uppercase', color: color.textFaint }}>{copy.auth.or}</span>
          <span style={{ flex: '1 1 0', height: 1, background: color.line }} />
        </div>
        <GoogleButton wide disabled={busy} onClick={() => { const google = auth.signInWithGoogle; if (google) void run(() => google.call(auth), onSignedIn); }} />
      </>}
      {(mode === 'forgot' || mode === 'reset' || mode === 'finish') && <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
        {mode === 'finish'
          ? <button type="button" className={cls('ctl', 'qb')} data-v1-auth-switch="" disabled={busy || !onSignOut} onClick={() => onSignOut?.()} style={quiet}>{copy.auth.signOut}</button>
          : <button type="button" className={cls('ctl', 'qb')} data-v1-auth-switch="" disabled={busy} onClick={mode === 'reset' ? leaveReset : () => go('signIn')} style={quiet}>{copy.auth.back}</button>}
      </div>}
    </form>
  </>;
}
